<template>
    <div class="user-turn flex flex-col items-end">
        <!-- MYAC-1269 — on a shared conversation, a colleague's question is named:
             the author the backend recorded on the message (never a raw uid). My
             own messages, and those written before sharing existed, carry no name. -->
        <span v-if="authorUid" class="user-turn-author mb-0.5 mr-1 text-xs text-surface-600" data-testid="user-message-author">
            <UserName :uid="authorUid" />
        </span>
        <div class="user-turn-row flex items-start gap-2 max-w-[80%] flex-row-reverse">
            <!-- MYAC-1151 — trimmed, not stripped: this bubble has a surface, so
                 it keeps real padding. The card called user messages "not too
                 bad", so this is a light touch. -->
            <div class="user-turn-bubble px-4 py-2.5 flex-1 bg-primary-50 rounded-2xl">
                <!-- MYAC-1182 — a small summary of the files THIS turn attached,
                     read back off the turn's own `__ATTACH__` marker (mirrors the
                     focus chip: plumbing stripped from the text, rendered instead
                     as its own line). Not shown on later turns — the transcript is
                     the record, so there is no lingering "still attached" chip. -->
                <div v-if="message.attachments?.length" class="mb-1 flex flex-wrap items-center gap-1 text-xs text-primary-700">
                    <i class="pi pi-paperclip shrink-0" aria-hidden="true" />
                    <span class="font-medium" :title="attachmentFilenames">{{ attachmentSummary }}</span>
                </div>
                <Markdown :content="getMessageText(message)" density="compact" verbatim />
            </div>
            <Button v-if="debug" :icon="isExpanded ? 'pi pi-eye-slash' : 'pi pi-code'" :text="true" :rounded="true"
                size="small" severity="secondary" @click="toggleRaw"
                v-tooltip.top="isExpanded ? 'Hide raw message' : 'Show raw message'" />
        </div>
        <div v-if="isExpanded" class="mt-2 max-w-[80%] w-full">
            <div class="bg-surface-50 border border-surface-300 rounded p-3 text-xs">
                <pre
                    class="whitespace-pre-wrap text-surface-700 overflow-auto max-h-64">{{ formatRawMessage(message) }}</pre>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type ChatbotMessage } from '@/stores/chatbot'
import Button from 'primevue/button'
import Markdown from '@/components/shared/Markdown.vue'
import UserName from '@/components/shared/users/UserName.vue'
import { useAppStore } from '@/stores/app'
import { useUsersStore } from '@/stores/users'

const appStore = useAppStore()
const debug = computed(() => appStore.debug)
const { t } = useI18n()

const props = defineProps<{
    message: ChatbotMessage
}>()

const authorUid = computed(() => {
    const uid = props.message.authorUid
    if (!uid) return null
    let me = ''
    try { me = useUsersStore().user?.uid ?? '' } catch { /* no signed-in person: name everyone */ }
    return uid === me ? null : uid
})

// MYAC-1034 — plural message, count passed twice (never 0 here — the chip is
// v-if'd on a non-empty list, so two forms suffice). The filenames themselves
// (spec §8) are the hover title rather than crowding the bubble.
const attachmentSummary = computed(() => {
    const n = props.message.attachments?.length ?? 0
    return t('chat.attachments.summary', { n }, n)
})
const attachmentFilenames = computed(() =>
    (props.message.attachments ?? []).map((f) => f.filename).join(', '))

const isExpanded = ref(false)

const toggleRaw = () => {
    isExpanded.value = !isExpanded.value
}

/**
 * Extract text content from a message's parts array
 * Filters out parts without text (e.g., function calls)
 */
const getMessageText = (message: ChatbotMessage): string => {
    return message.parts
        .filter(part => part.text !== undefined)
        .map(part => part.text!)
        .join('')
}

/**
 * Format raw message as JSON for display
 * Shows the raw Event data (including thinking parts) if available, otherwise shows the message
 */
const formatRawMessage = (message: ChatbotMessage): string => {
    // Show raw Event data if available (includes all parts with thinking parts)
    // Otherwise show the filtered message
    return JSON.stringify(message.raw || message, null, 2)
}
</script>
